/**
 * Space-themed notification sounds using Web Audio API synthesis.
 * These sounds are designed to feel like sci-fi UI elements - short,
 * pleasant, and futuristic.
 */

type SoundId = 'chime' | 'bell' | 'subtle' | 'victory' | 'none'

// Shared AudioContext (created lazily to comply with autoplay policies)
let audioContext: AudioContext | null = null

function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new AudioContext()
  }
  return audioContext
}

/**
 * Creates a space-themed chime sound.
 * High-pitched oscillator sweep with reverb-like decay - think sci-fi UI beep.
 */
function playChime(ctx: AudioContext): void {
  const now = ctx.currentTime

  // Main oscillator with frequency sweep
  const osc1 = ctx.createOscillator()
  osc1.type = 'sine'
  osc1.frequency.setValueAtTime(1200, now)
  osc1.frequency.exponentialRampToValueAtTime(800, now + 0.15)

  // Secondary oscillator for shimmer
  const osc2 = ctx.createOscillator()
  osc2.type = 'sine'
  osc2.frequency.setValueAtTime(1800, now)
  osc2.frequency.exponentialRampToValueAtTime(1200, now + 0.1)

  // Gain envelope for main oscillator
  const gain1 = ctx.createGain()
  gain1.gain.setValueAtTime(0, now)
  gain1.gain.linearRampToValueAtTime(0.3, now + 0.02)
  gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.4)

  // Gain envelope for secondary oscillator
  const gain2 = ctx.createGain()
  gain2.gain.setValueAtTime(0, now)
  gain2.gain.linearRampToValueAtTime(0.15, now + 0.01)
  gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.2)

  // High-pass filter for that clean sci-fi feel
  const filter = ctx.createBiquadFilter()
  filter.type = 'highpass'
  filter.frequency.setValueAtTime(400, now)

  // Connect the nodes
  osc1.connect(gain1)
  osc2.connect(gain2)
  gain1.connect(filter)
  gain2.connect(filter)
  filter.connect(ctx.destination)

  // Start and stop
  osc1.start(now)
  osc2.start(now)
  osc1.stop(now + 0.5)
  osc2.stop(now + 0.3)
}

/**
 * Creates a space-themed bell sound.
 * FM synthesis bell with metallic timbre - think starship notification.
 */
function playBell(ctx: AudioContext): void {
  const now = ctx.currentTime

  // Carrier oscillator
  const carrier = ctx.createOscillator()
  carrier.type = 'sine'
  carrier.frequency.setValueAtTime(880, now)

  // Modulator oscillator (FM synthesis)
  const modulator = ctx.createOscillator()
  modulator.type = 'sine'
  modulator.frequency.setValueAtTime(880 * 2.5, now) // Ratio for bell-like timbre

  // Modulator gain (modulation depth)
  const modGain = ctx.createGain()
  modGain.gain.setValueAtTime(200, now)
  modGain.gain.exponentialRampToValueAtTime(1, now + 0.6)

  // Main gain envelope
  const mainGain = ctx.createGain()
  mainGain.gain.setValueAtTime(0, now)
  mainGain.gain.linearRampToValueAtTime(0.25, now + 0.01)
  mainGain.gain.exponentialRampToValueAtTime(0.01, now + 0.7)

  // Add some harmonics with another oscillator
  const harmonic = ctx.createOscillator()
  harmonic.type = 'sine'
  harmonic.frequency.setValueAtTime(880 * 3, now)

  const harmonicGain = ctx.createGain()
  harmonicGain.gain.setValueAtTime(0, now)
  harmonicGain.gain.linearRampToValueAtTime(0.08, now + 0.01)
  harmonicGain.gain.exponentialRampToValueAtTime(0.01, now + 0.3)

  // Connect FM synthesis
  modulator.connect(modGain)
  modGain.connect(carrier.frequency)

  // Connect to output
  carrier.connect(mainGain)
  harmonic.connect(harmonicGain)
  mainGain.connect(ctx.destination)
  harmonicGain.connect(ctx.destination)

  // Start and stop
  carrier.start(now)
  modulator.start(now)
  harmonic.start(now)
  carrier.stop(now + 0.8)
  modulator.stop(now + 0.8)
  harmonic.stop(now + 0.4)
}

/**
 * Creates a subtle space-themed ping sound.
 * Soft sine wave ping with quick decay - think quiet radar ping.
 */
function playSubtle(ctx: AudioContext): void {
  const now = ctx.currentTime

  // Single soft oscillator
  const osc = ctx.createOscillator()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(600, now)
  osc.frequency.exponentialRampToValueAtTime(400, now + 0.15)

  // Soft gain envelope
  const gain = ctx.createGain()
  gain.gain.setValueAtTime(0, now)
  gain.gain.linearRampToValueAtTime(0.15, now + 0.01)
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.25)

  // Low-pass filter for softness
  const filter = ctx.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.setValueAtTime(800, now)
  filter.Q.setValueAtTime(1, now)

  // Connect
  osc.connect(filter)
  filter.connect(gain)
  gain.connect(ctx.destination)

  // Start and stop
  osc.start(now)
  osc.stop(now + 0.3)
}

/**
 * Creates a sci-fi confirmation sound - LCARS-style computer beeps with
 * a synthesized ascending whoosh and resonant pad tail.
 * Duration: ~1.5 seconds
 */
function playVictory(ctx: AudioContext): void {
  const now = ctx.currentTime

  // Master gain
  const masterGain = ctx.createGain()
  masterGain.gain.setValueAtTime(0.25, now)
  masterGain.connect(ctx.destination)

  // Ping-pong delay for spacey echo
  const delay1 = ctx.createDelay()
  delay1.delayTime.setValueAtTime(0.12, now)
  const delay2 = ctx.createDelay()
  delay2.delayTime.setValueAtTime(0.24, now)
  const delayGain1 = ctx.createGain()
  delayGain1.gain.setValueAtTime(0.25, now)
  const delayGain2 = ctx.createGain()
  delayGain2.gain.setValueAtTime(0.15, now)
  delay1.connect(delayGain1)
  delayGain1.connect(masterGain)
  delayGain1.connect(delay2)
  delay2.connect(delayGain2)
  delayGain2.connect(masterGain)

  // --- Layer 1: LCARS-style confirmation beeps (two quick tones) ---
  const beepFreqs = [1318.5, 1760.0] // E6, A6 - bright computer tones
  beepFreqs.forEach((freq, i) => {
    const t = now + i * 0.1
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(freq, t)

    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0, t)
    gain.gain.linearRampToValueAtTime(0.35, t + 0.008)
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.12)

    osc.connect(gain)
    gain.connect(masterGain)
    gain.connect(delay1)
    osc.start(t)
    osc.stop(t + 0.2)
  })

  // --- Layer 2: Ascending frequency sweep (electronic whoosh) ---
  const sweepStart = now + 0.15
  const sweepOsc = ctx.createOscillator()
  sweepOsc.type = 'sawtooth'
  sweepOsc.frequency.setValueAtTime(200, sweepStart)
  sweepOsc.frequency.exponentialRampToValueAtTime(2400, sweepStart + 0.4)

  // Bandpass filter to shape the whoosh
  const sweepFilter = ctx.createBiquadFilter()
  sweepFilter.type = 'bandpass'
  sweepFilter.frequency.setValueAtTime(400, sweepStart)
  sweepFilter.frequency.exponentialRampToValueAtTime(3000, sweepStart + 0.4)
  sweepFilter.Q.setValueAtTime(2, sweepStart)

  const sweepGain = ctx.createGain()
  sweepGain.gain.setValueAtTime(0, sweepStart)
  sweepGain.gain.linearRampToValueAtTime(0.15, sweepStart + 0.05)
  sweepGain.gain.exponentialRampToValueAtTime(0.01, sweepStart + 0.45)

  sweepOsc.connect(sweepFilter)
  sweepFilter.connect(sweepGain)
  sweepGain.connect(masterGain)
  sweepOsc.start(sweepStart)
  sweepOsc.stop(sweepStart + 0.5)

  // --- Layer 3: Resonant synth confirmation tone ---
  const toneStart = now + 0.35
  const toneFreq = 880 // A5

  // Detuned pair for width
  const toneOsc1 = ctx.createOscillator()
  toneOsc1.type = 'square'
  toneOsc1.frequency.setValueAtTime(toneFreq, toneStart)

  const toneOsc2 = ctx.createOscillator()
  toneOsc2.type = 'square'
  toneOsc2.frequency.setValueAtTime(toneFreq * 1.005, toneStart) // slight detune

  // Low-pass filter for that warm synth character
  const toneLPF = ctx.createBiquadFilter()
  toneLPF.type = 'lowpass'
  toneLPF.frequency.setValueAtTime(3000, toneStart)
  toneLPF.frequency.exponentialRampToValueAtTime(600, toneStart + 0.8)
  toneLPF.Q.setValueAtTime(4, toneStart)

  const toneGain = ctx.createGain()
  toneGain.gain.setValueAtTime(0, toneStart)
  toneGain.gain.linearRampToValueAtTime(0.12, toneStart + 0.02)
  toneGain.gain.setValueAtTime(0.10, toneStart + 0.3)
  toneGain.gain.exponentialRampToValueAtTime(0.01, toneStart + 0.9)

  toneOsc1.connect(toneLPF)
  toneOsc2.connect(toneLPF)
  toneLPF.connect(toneGain)
  toneGain.connect(masterGain)
  toneGain.connect(delay1)

  toneOsc1.start(toneStart)
  toneOsc2.start(toneStart)
  toneOsc1.stop(toneStart + 1.0)
  toneOsc2.stop(toneStart + 1.0)

  // --- Layer 4: High shimmer pad (sci-fi sparkle tail) ---
  const shimmerStart = now + 0.4
  const shimmerOsc = ctx.createOscillator()
  shimmerOsc.type = 'sine'
  shimmerOsc.frequency.setValueAtTime(2637, shimmerStart) // E7
  // Gentle vibrato
  const lfo = ctx.createOscillator()
  lfo.frequency.setValueAtTime(6, shimmerStart)
  const lfoGain = ctx.createGain()
  lfoGain.gain.setValueAtTime(15, shimmerStart)
  lfo.connect(lfoGain)
  lfoGain.connect(shimmerOsc.frequency)

  const shimmerGain = ctx.createGain()
  shimmerGain.gain.setValueAtTime(0, shimmerStart)
  shimmerGain.gain.linearRampToValueAtTime(0.06, shimmerStart + 0.1)
  shimmerGain.gain.exponentialRampToValueAtTime(0.01, shimmerStart + 1.0)

  shimmerOsc.connect(shimmerGain)
  shimmerGain.connect(masterGain)
  shimmerGain.connect(delay1)

  shimmerOsc.start(shimmerStart)
  lfo.start(shimmerStart)
  shimmerOsc.stop(shimmerStart + 1.1)
  lfo.stop(shimmerStart + 1.1)
}

// ==========================================
// CORPORATE SOUNDS
// Clean, understated notification tones
// suitable for a professional environment.
// ==========================================

/**
 * Corporate chime: clean two-note major interval (Teams/Slack style).
 */
function playCorporateChime(ctx: AudioContext): void {
  const now = ctx.currentTime

  const notes = [523.25, 659.25] // C5, E5 — major third
  notes.forEach((freq, i) => {
    const t = now + i * 0.12
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(freq, t)

    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0, t)
    gain.gain.linearRampToValueAtTime(0.2, t + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.25)

    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(t)
    osc.stop(t + 0.3)
  })
}

/**
 * Corporate bell: warm single tone with gentle decay (email notification style).
 */
function playCorporateBell(ctx: AudioContext): void {
  const now = ctx.currentTime

  const osc = ctx.createOscillator()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(784, now) // G5

  const gain = ctx.createGain()
  gain.gain.setValueAtTime(0, now)
  gain.gain.linearRampToValueAtTime(0.2, now + 0.01)
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5)

  // Gentle low-pass for warmth
  const filter = ctx.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.setValueAtTime(2000, now)

  osc.connect(filter)
  filter.connect(gain)
  gain.connect(ctx.destination)
  osc.start(now)
  osc.stop(now + 0.6)
}

/**
 * Corporate subtle: soft low click, barely there.
 */
function playCorporateSubtle(ctx: AudioContext): void {
  const now = ctx.currentTime

  const osc = ctx.createOscillator()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(440, now) // A4
  osc.frequency.exponentialRampToValueAtTime(350, now + 0.08)

  const gain = ctx.createGain()
  gain.gain.setValueAtTime(0, now)
  gain.gain.linearRampToValueAtTime(0.1, now + 0.005)
  gain.gain.exponentialRampToValueAtTime(0.01, now + 0.12)

  const filter = ctx.createBiquadFilter()
  filter.type = 'lowpass'
  filter.frequency.setValueAtTime(600, now)

  osc.connect(filter)
  filter.connect(gain)
  gain.connect(ctx.destination)
  osc.start(now)
  osc.stop(now + 0.15)
}

/**
 * Corporate victory: ascending major triad — clean, satisfied, done.
 */
function playCorporateVictory(ctx: AudioContext): void {
  const now = ctx.currentTime

  const masterGain = ctx.createGain()
  masterGain.gain.setValueAtTime(0.2, now)
  masterGain.connect(ctx.destination)

  // C5 → E5 → G5 ascending triad
  const notes = [523.25, 659.25, 784.0]
  notes.forEach((freq, i) => {
    const t = now + i * 0.15
    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(freq, t)

    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0, t)
    gain.gain.linearRampToValueAtTime(0.25, t + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.01, t + 0.4)

    osc.connect(gain)
    gain.connect(masterGain)
    osc.start(t)
    osc.stop(t + 0.5)
  })
}

// ==========================================
// Playback
// ==========================================

function isCorporateTheme(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('theme-corporate')
}

/**
 * Plays a notification sound by ID.
 * Corporate theme gets clean, professional tones; sci-fi themes get the originals.
 */
export function playNotificationSound(soundId: string): void {
  if (soundId === 'none') {
    return
  }

  try {
    const ctx = getAudioContext()

    // Resume context if suspended (browser autoplay policy)
    if (ctx.state === 'suspended') {
      ctx.resume()
    }

    const corporate = isCorporateTheme()

    switch (soundId as SoundId) {
      case 'chime':
        corporate ? playCorporateChime(ctx) : playChime(ctx)
        break
      case 'bell':
        corporate ? playCorporateBell(ctx) : playBell(ctx)
        break
      case 'subtle':
        corporate ? playCorporateSubtle(ctx) : playSubtle(ctx)
        break
      case 'victory':
        corporate ? playCorporateVictory(ctx) : playVictory(ctx)
        break
      default:
        corporate ? playCorporateChime(ctx) : playChime(ctx)
    }
  } catch (error) {
    // Silently fail if Web Audio API is not available
    console.warn('Failed to play notification sound:', error)
  }
}

/**
 * Plays a preview of a sound (same as playNotificationSound but with explicit naming)
 * @param soundId - The sound to preview
 */
export function playPreview(soundId: string): void {
  playNotificationSound(soundId)
}
